import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  BarChart as BarChartIcon,
  CheckCircle as CheckCircleIcon,
  People as PeopleIcon,
  Quiz as QuizIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { adminFetch } from '../utils/adminFetch';
import { getAdminCourseName } from '../utils/courseDisplay';

interface CourseAnalyticsData {
  course: {
    id: string;
    courseProject?: string;
    title: { [key: string]: string };
    defaultLanguage?: string;
    totalChapters: number;
    totalItems: number;
    totalQuizzes: number;
  };
  filter: { groupId: string | null; groupName: string | null };
  groups: Array<{ id: string; name: string; studentCount?: number }>;
  overview: {
    enrolledStudents: number;
    completedStudents: number;
    completionRate: number | null;
    avgProgress: number | null;
    avgChapterRating: number | null;
    chapterRatingCount: number;
    avgQuizScore: number | null;
    quizPassRate: number | null;
    quizResponseCount: number;
    avgTimePerStudentMinutes: number | null;
    avgTimePerItemMinutes: number | null;
    avgWorkshopRating: number | null;
    workshopRatingCount: number;
  };
  chapters: Array<{
    chapterId: string;
    chapterOrder: number;
    chapterTitle: string;
    itemCount: number;
    avgRating: number | null;
    ratingCount: number;
    avgCompletionRate: number | null;
    chapterCompletionRate: number | null;
    studentsWithProgress: number;
  }>;
  quizzes: Array<{
    quizId: string;
    chapterId: string;
    chapterOrder: number;
    chapterTitle: string;
    itemOrder: number;
    title: string;
    isDiagnostic: boolean;
    responseCount: number;
    avgScore: number | null;
    passRate: number | null;
  }>;
  itemsTime: Array<{
    itemId: string;
    chapterId: string;
    chapterOrder: number;
    chapterTitle: string;
    itemOrder: number;
    itemType: string;
    title: string;
    avgTimeMinutes: number | null;
    totalTimeMinutes: number;
    learnerCount: number;
  }>;
  timeByItemType: Array<{
    itemType: string;
    avgTimeMinutes: number | null;
    totalTimeMinutes: number;
  }>;
}

const fmt = (value: number | null | undefined, suffix = ''): string => {
  if (value === null || value === undefined) return '—';
  return `${value}${suffix}`;
};

const itemTypeLabel = (type: string): string => {
  switch (type) {
    case 'capsule':
      return 'Capsule';
    case 'quiz':
      return 'Quiz';
    case 'workshop':
      return 'Atelier';
    default:
      return type;
  }
};

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}> = ({ icon, label, value, hint }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        {icon}
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Typography variant="h5" fontWeight={700}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const CourseAnalytics: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedGroupId = searchParams.get('groupId') || '';

  const [data, setData] = useState<CourseAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchAnalytics = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    setError('');
    try {
      const qs = selectedGroupId ? `?groupId=${encodeURIComponent(selectedGroupId)}` : '';
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/courses/${courseId}/analytics${qs}`
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Impossible de charger les statistiques');
      }
      setData(await response.json());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  }, [courseId, selectedGroupId]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const setGroupFilter = (groupId: string) => {
    if (groupId) {
      setSearchParams({ groupId });
    } else {
      setSearchParams({});
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Données indisponibles'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Retour
        </Button>
      </Container>
    );
  }

  const courseName = getAdminCourseName(data.course);
  const { overview: o } = data;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mb: 1 }}
          >
            Retour
          </Button>
          <Typography variant="h5" fontWeight={700}>
            Statistiques — {courseName}
          </Typography>
          {data.course.courseProject && (
            <Typography variant="body2" color="text.secondary">
              {data.course.courseProject}
            </Typography>
          )}
        </Box>
        <Button
          variant="outlined"
          startIcon={<PeopleIcon />}
          onClick={() =>
            navigate(
              selectedGroupId
                ? `/courses/${courseId}/students?groupId=${selectedGroupId}`
                : `/courses/${courseId}/students`
            )
          }
        >
          Voir les apprenants
        </Button>
      </Box>

      {data.groups.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={1} mb={3}>
          <Chip
            label="Tous les groupes"
            clickable
            color={selectedGroupId === '' ? 'primary' : 'default'}
            variant={selectedGroupId === '' ? 'filled' : 'outlined'}
            onClick={() => setGroupFilter('')}
          />
          {data.groups.map((g) => (
            <Chip
              key={g.id}
              label={
                typeof g.studentCount === 'number' ? `${g.name} (${g.studentCount})` : g.name
              }
              clickable
              color={selectedGroupId === g.id ? 'primary' : 'default'}
              variant={selectedGroupId === g.id ? 'filled' : 'outlined'}
              onClick={() => setGroupFilter(g.id)}
            />
          ))}
        </Stack>
      )}

      {selectedGroupId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Filtre actif : <strong>{data.filter.groupName || 'Groupe'}</strong> ({o.enrolledStudents}{' '}
          apprenant{o.enrolledStudents > 1 ? 's' : ''})
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 2,
          mb: 3,
        }}
      >
        <StatCard
          icon={<PeopleIcon color="primary" fontSize="small" />}
          label="Apprenants inscrits"
          value={String(o.enrolledStudents)}
          hint={`${o.completedStudents} ont terminé (${fmt(o.completionRate, '%')})`}
        />
        <StatCard
          icon={<TrendingUpIcon color="primary" fontSize="small" />}
          label="Progression moyenne"
          value={fmt(o.avgProgress, '%')}
          hint={`${data.course.totalItems} items au total`}
        />
        <StatCard
          icon={<StarIcon color="warning" fontSize="small" />}
          label="Note moy. sections"
          value={fmt(o.avgChapterRating, '/10')}
          hint={`${o.chapterRatingCount} notation${o.chapterRatingCount > 1 ? 's' : ''}`}
        />
        <StatCard
          icon={<QuizIcon color="secondary" fontSize="small" />}
          label="Score moy. quiz"
          value={fmt(o.avgQuizScore, '%')}
          hint={`Réussite : ${fmt(o.quizPassRate, '%')} · ${o.quizResponseCount} réponses`}
        />
        <StatCard
          icon={<ScheduleIcon color="action" fontSize="small" />}
          label="Temps moy. / apprenant"
          value={fmt(o.avgTimePerStudentMinutes, ' min')}
          hint={`${fmt(o.avgTimePerItemMinutes, ' min')} / item en moyenne`}
        />
        {o.avgWorkshopRating !== null && (
          <StatCard
            icon={<CheckCircleIcon color="success" fontSize="small" />}
            label="Note moy. ateliers"
            value={fmt(o.avgWorkshopRating, '/5')}
            hint={`${o.workshopRatingCount} évaluation${o.workshopRatingCount > 1 ? 's' : ''}`}
          />
        )}
      </Box>

      <Typography variant="h6" fontWeight={700} mb={1}>
        Sections
      </Typography>
      <TableContainer component={Card} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Section</TableCell>
              <TableCell align="right">Note moy.</TableCell>
              <TableCell align="right">Notations</TableCell>
              <TableCell align="right">Progression moy.</TableCell>
              <TableCell align="right">Complétion section</TableCell>
              <TableCell align="right">Apprenants actifs</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.chapters.map((ch) => (
              <TableRow key={ch.chapterId}>
                <TableCell>
                  {ch.chapterOrder}. {ch.chapterTitle}
                  <Typography variant="caption" color="text.secondary" display="block">
                    {ch.itemCount} item{ch.itemCount > 1 ? 's' : ''}
                  </Typography>
                </TableCell>
                <TableCell align="right">{fmt(ch.avgRating, '/10')}</TableCell>
                <TableCell align="right">{ch.ratingCount || '—'}</TableCell>
                <TableCell align="right">{fmt(ch.avgCompletionRate, '%')}</TableCell>
                <TableCell align="right">{fmt(ch.chapterCompletionRate, '%')}</TableCell>
                <TableCell align="right">{ch.studentsWithProgress}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" fontWeight={700} mb={1}>
        Quiz
      </Typography>
      <TableContainer component={Card} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Quiz</TableCell>
              <TableCell>Section</TableCell>
              <TableCell align="right">Réponses</TableCell>
              <TableCell align="right">Score moy.</TableCell>
              <TableCell align="right">Taux de réussite</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.quizzes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    Aucun quiz dans cette formation.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.quizzes.map((q) => (
                <TableRow key={q.quizId}>
                  <TableCell>
                    {q.title}
                    {q.isDiagnostic && (
                      <Chip label="Diagnostique" size="small" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {q.chapterOrder}. {q.chapterTitle}
                  </TableCell>
                  <TableCell align="right">{q.responseCount}</TableCell>
                  <TableCell align="right">
                    {q.isDiagnostic ? '—' : fmt(q.avgScore, '%')}
                  </TableCell>
                  <TableCell align="right">
                    {q.isDiagnostic ? '—' : fmt(q.passRate, '%')}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" fontWeight={700} mb={1}>
        Temps passé par item
      </Typography>
      <TableContainer component={Card} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Section</TableCell>
              <TableCell align="right">Temps moy.</TableCell>
              <TableCell align="right">Temps total</TableCell>
              <TableCell align="right">Apprenants</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.itemsTime.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">
                    Pas encore de données de temps.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.itemsTime.map((it) => (
                <TableRow key={it.itemId}>
                  <TableCell>{it.title}</TableCell>
                  <TableCell>{itemTypeLabel(it.itemType)}</TableCell>
                  <TableCell>
                    {it.chapterOrder}. {it.chapterTitle}
                  </TableCell>
                  <TableCell align="right">{fmt(it.avgTimeMinutes, ' min')}</TableCell>
                  <TableCell align="right">{it.totalTimeMinutes} min</TableCell>
                  <TableCell align="right">{it.learnerCount}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {data.timeByItemType.length > 0 && (
        <>
          <Typography variant="h6" fontWeight={700} mb={1}>
            Temps par type d&apos;item
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
            {data.timeByItemType.map((t) => (
              <Chip
                key={t.itemType}
                icon={<BarChartIcon />}
                label={`${itemTypeLabel(t.itemType)} : ${fmt(t.avgTimeMinutes, ' min')} moy. · ${t.totalTimeMinutes} min total`}
                variant="outlined"
              />
            ))}
          </Stack>
        </>
      )}
    </Container>
  );
};

export default CourseAnalytics;
